import React from "react";
import {
  useV2ListNotificationsQuery,
  useV2UpdateNotificationMutation,
  useV2NotificationsMarkAllReadMutation,
} from "@generated";
import formatRelativeTime from "./formatRelativeTime";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";

interface NotificationDropdownProps {
  // "left" anchors the panel's left edge to its positioned ancestor (for react nav);
  align?: "left" | "right";
  // Full style override/merge — used by callers that compute their own viewport position
  style?: React.CSSProperties;
  refetchUnreadCount: () => void;
}

const NotificationDropdown = React.forwardRef<
  HTMLDivElement,
  NotificationDropdownProps
>(({ align = "right", style, refetchUnreadCount }, ref) => {
  const { data, isLoading, refetch } = useV2ListNotificationsQuery({
    page: { size: 5 },
  });
  const [updateNotification] = useV2UpdateNotificationMutation();
  const [markAllRead] = useV2NotificationsMarkAllReadMutation();
  const notifications = data?.data ?? [];

  const defaultPositionStyle: React.CSSProperties = {
    position: "absolute",
    top: "100%",
    marginTop: 8,
    ...(align === "right" ? { right: 0 } : { left: 0 }),
  };

  const handleView = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string | undefined,
    link: string
  ) => {
    e.preventDefault();

    if (!id) {
      window.location.href = link;
      return;
    }

    try {
      await updateNotification({
        id,
        body: {
          data: {
            id,
            type: "notifications",
            attributes: { "read-at": new Date().toISOString() },
          },
        },
      }).unwrap();
    } catch {
      // Still navigate even if marking read failed — that shouldn't block
      // the user from viewing the notification.
    } finally {
      window.location.href = link;
    }
  };

  const handleClearAll = async () => {
    await markAllRead().unwrap();
    refetch();
    refetchUnreadCount();
  };

  return (
    <div
      ref={ref}
      data-notification-panel
      style={{
        ...defaultPositionStyle,
        width: 340,
        maxWidth: "90vw",
        backgroundColor: "#fff",
        borderRadius: 6,
        border: "1px solid #e0e0e0",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        overflow: "hidden",
        zIndex: 1000,
        ...style,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <span />
        <button
          type="button"
          onClick={handleClearAll}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: "#418172",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Clear All
        </button>
      </div>
      {isLoading ? (
        <SmallSpinner />
      ) : notifications.length === 0 ? (
        <div style={{ padding: 16, color: "#6c757d", fontSize: 14 }}>
          No notifications
        </div>
      ) : (
        notifications.map((notification) => {
          const attributes = notification.attributes;
          const isUnread = !attributes["read-at"];

          return (
            <div
              key={notification.id}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                padding: "12px 16px",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  marginTop: 6,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#418172",
                  visibility: isUnread ? "visible" : "hidden",
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      color: "#1c2655",
                      fontWeight: isUnread ? 700 : 400,
                      fontSize: 14,
                    }}
                  >
                    {attributes.body}
                  </span>
                  {attributes.link && (
                    <a
                      href={attributes.link}
                      onClick={(e) =>
                        handleView(
                          e,
                          notification.id,
                          attributes.link as string
                        )
                      }
                      style={{
                        color: "#418172",
                        fontSize: 13,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      View
                    </a>
                  )}
                </div>
                {attributes["created-at"] && (
                  <div style={{ color: "#6c757d", fontSize: 12, marginTop: 2 }}>
                    {formatRelativeTime(attributes["created-at"])}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
});

NotificationDropdown.displayName = "NotificationDropdown";

export default NotificationDropdown;
